import { yupResolver } from '@hookform/resolvers/yup';
import { Button, ControlledInput, Entering, Typography } from 'components';
import { BillingInfoInput, CountryCodeEnum, CurrencyEnum } from 'gql/graphql';
import { onErrorHelper } from 'handlers/handlerMutations';
import { useTranslations } from 'next-intl';
import { useBillingAccount } from 'providers/BillingInfoProviders/useBillingAccount';
import { useBillingInfoUpdate } from 'providers/BillingInfoProviders/useBillingInfoUpdate';
import { useCurrencies } from 'providers/BillingInfoProviders/useCurrencies';
import useCountry from 'providers/CountryProviders/useCountry';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { TGenericError } from 'types/TGenericError';
import { TScheme } from 'types/TScheme';
import * as yup from 'yup';
import YupPassword from 'yup-password';

import { handlerEnter } from 'handlers/handlerEnter';
import { toaster } from 'helpers/toaster';
import { InfoHollowIcon } from 'lib/svgs/Icons';
import { notifyException } from 'services/appsignal';
import 'styles/sass/split.scss';

YupPassword(yup);

const BillingInfoForm = () => {
  const t = useTranslations();

  const schema = yup.object().shape<TScheme<BillingInfoInput>>({
    firstName: yup.string().required(t('messages.fields.first_name_required')),
    lastName: yup.string().required(t('messages.fields.last_name_required')),
    country: yup.string().required(t('messages.fields.country_required')),
    city: yup.string().required(t('messages.fields.city_required')),
    address: yup.string().required(t('messages.fields.address_required')),
    invoiceCcEmails: yup.array().of(yup.string().email(t('messages.fields.email_validation'))),
    invoiceEmails: yup
      .array()
      .of(
        yup
          .string()
          .test(
            'email-validation',
            t('messages.fields.email_validation'),
            function (value, context) {
              const email = yup
                .string()
                .email(t('messages.fields.email_validation'))
                .required(t('messages.fields.invoice_emails_required'))
                .isValidSync(value);
              const nullable = yup
                .string()
                .test('empty', function (value) {
                  return value === '';
                })
                .notRequired()
                .isValidSync(value);

              if ((context?.options as { index: number })?.index === 0) return email;

              return email || nullable;
            }
          )
      )
      .min(1, t('messages.fields.invoice_email_least_one'))
      .required(t('messages.fields.invoice_emails_required')),
    postalCode: yup.string().required(t('messages.fields.postal_code_required')),
    business: yup.boolean().required(t('messages.fields.business_required')),
    businessName: yup.string().when('business', {
      is: true,
      then: yup.string().required(t('messages.fields.business_name_required')),
      otherwise: yup.string()
    }),
    taxid: yup.string().when('business', {
      is: true,
      then: yup.string().required(t('messages.fields.tax_id_required')),
      otherwise: yup.string()
    }),
    currency: yup.string().required(t('messages.fields.currency_required'))
  });

  const { data: optionsCountry, isFetched: isFetchedCountry } = useCountry();
  const {
    data: billingAccountPayload,
    isLoading,
    refetch,
    isFetched: isFetchedBilling
  } = useBillingAccount({
    staleTime: 0
  });

  const { mutateAsync: billingInfoUpdate } = useBillingInfoUpdate({
    onSuccess: () => toaster.success(t('messages.success.updated_billing')),
    onError: onErrorHelper as (data: TGenericError | string) => void
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    getValues,
    watch,
    reset
  } = useForm<BillingInfoInput>({
    resolver: yupResolver(schema),
    defaultValues: {
      firstName: '',
      lastName: '',
      business: false,
      businessName: '',
      taxid: '',
      country: '' as CountryCodeEnum,
      city: String(''),
      postalCode: '',
      address: '',
      invoiceEmails: [''],
      invoiceCcEmails: [''],
      currency: ''
    }
  });

  const { data: currencies } = useCurrencies();

  const onUpdate = useCallback(async () => {
    try {
      const billingInfo = getValues();
      const invoiceCcEmails = billingInfo.invoiceCcEmails?.filter((email) => !!email) ?? [];
      const response = await billingInfoUpdate({
        ...billingInfo,
        invoiceCcEmails
      });

      if (response.success) {
        setTimeout(() => {
          refetch();
        }, 1000);
      }
    } catch (error) {
      notifyException(error);
    }
  }, [billingInfoUpdate, getValues, refetch]);

  const onSubmit = handleSubmit(onUpdate);

  const isCompany = watch('business');

  const defaultOptionCountry = optionsCountry?.find(
    (option) => option.value === billingAccountPayload?.billingInfo?.country
  );

  const optionsBusiness = [
    { label: t('billings.for_business'), value: true },
    { label: t('billings.personal_use'), value: false }
  ];

  const defaultOptionBusiness = optionsBusiness.find(
    (option) => option.value === billingAccountPayload?.billingInfo?.business
  );

  const optionsCurrency = currencies?.allowedClientCurencies?.map((currency) => ({
    label: currency,
    value: currency as CurrencyEnum
  }));

  const defaultOptionCurrency = optionsCurrency?.find(
    (option) => option.value === billingAccountPayload?.currency
  );

  useEffect(() => {
    if (isFetchedCountry && isFetchedBilling)
      reset({
        firstName: billingAccountPayload?.billingInfo?.firstName || '',
        lastName: billingAccountPayload?.billingInfo?.lastName || '',
        business: billingAccountPayload?.billingInfo?.business || false,
        businessName: billingAccountPayload?.billingInfo?.businessName || '',
        taxid: billingAccountPayload?.billingInfo?.taxid || '',
        country: (billingAccountPayload?.billingInfo?.country || '') as CountryCodeEnum,
        city: String(billingAccountPayload?.billingInfo?.city || ''),
        postalCode: billingAccountPayload?.billingInfo?.postalCode || '',
        address: billingAccountPayload?.billingInfo?.address || '',
        invoiceEmails: !!billingAccountPayload?.billingInfo?.invoiceEmails.length
          ? billingAccountPayload?.billingInfo?.invoiceEmails
          : [''],
        invoiceCcEmails: billingAccountPayload?.billingInfo?.invoiceCcEmails?.length
          ? billingAccountPayload?.billingInfo?.invoiceCcEmails
          : [''],
        currency: billingAccountPayload?.currency || ''
      });
  }, [
    billingAccountPayload?.billingInfo?.address,
    billingAccountPayload?.billingInfo?.business,
    billingAccountPayload?.billingInfo?.businessName,
    billingAccountPayload?.billingInfo?.city,
    billingAccountPayload?.billingInfo?.country,
    billingAccountPayload?.billingInfo?.firstName,
    billingAccountPayload?.billingInfo?.invoiceCcEmails,
    billingAccountPayload?.billingInfo?.invoiceEmails,
    billingAccountPayload?.billingInfo?.lastName,
    billingAccountPayload?.billingInfo?.postalCode,
    billingAccountPayload?.billingInfo?.taxid,
    billingAccountPayload?.currency,
    isFetchedBilling,
    isFetchedCountry,
    reset
  ]);

  return (
    <Entering
      offset={5}
      delay={0.6}
      duration={0.2}
      position="bottom"
      className="mb-2 w-[62.5%] rounded-md bg-white p-4 shadow-custom"
    >
      <div className="mb-2">
        <div className="flex">
          <Typography variant="medium1" className={'!m-0 !leading-10 text-tertiary-100'}>
            {t('billings.show.billing_info')}
          </Typography>
          <a
            data-tooltip-id="tolq-info-top-start"
            data-tooltip-html={t.raw('billings.change_payment_method')}
            className="w-8"
          >
            <InfoHollowIcon className="origin-[10px] scale-50 cursor-pointer" />
          </a>
        </div>
        <hr className="mt-1 border !border-t-0 border-tertiary-600" />
      </div>
      <div className="split">
        <ControlledInput
          control={control}
          error={errors.firstName}
          label={
            <>
              {t('billings.show.first_name')}
              <span className="text-secondary-red">*</span>
            </>
          }
          type="text"
          name="firstName"
          classNameParent="w-full"
          className="w-full"
        />
        <ControlledInput
          control={control}
          error={errors.lastName}
          label={
            <>
              {t('billings.show.last_name')}
              <span className="text-secondary-red">*</span>
            </>
          }
          type="text"
          name="lastName"
          classNameParent="w-full"
          className="w-full"
        />
      </div>
      <div className="split">
        <ControlledInput
          control={control}
          error={errors.country}
          label={
            <>
              {t('billings.show.country')}
              <span className="text-secondary-red">*</span>
            </>
          }
          options={optionsCountry}
          defaultOption={defaultOptionCountry}
          type="combobox"
          name="country"
          placeholder={t('billings.show.select_country')}
          classNameParent="w-full"
          className="w-full"
        />
        <ControlledInput
          control={control}
          error={errors.city}
          label={
            <>
              {t('billings.show.city')}
              <span className="text-secondary-red">*</span>
            </>
          }
          type="text"
          name="city"
          classNameParent="w-full"
          className="w-full"
        />
      </div>

      <div className="split">
        <ControlledInput
          control={control}
          error={errors.postalCode}
          label={
            <>
              {t('billings.show.postal_code')}
              <span className="text-secondary-red">*</span>
            </>
          }
          type="text"
          name="postalCode"
          classNameParent="w-full"
          className="w-full"
        />
        <ControlledInput
          control={control}
          error={errors.address}
          label={
            <>
              {t('billings.show.street_address')}
              <span className="text-secondary-red">*</span>
            </>
          }
          type="text"
          name="address"
          classNameParent="w-full"
          className="w-full"
        />
      </div>
      <ControlledInput
        control={control}
        error={errors.business}
        label={t('billings.account_type')}
        type="select"
        options={optionsBusiness}
        defaultOption={defaultOptionBusiness}
        name="business"
        classNameParent="mb-2"
      />
      {isCompany && (
        <div className="split">
          <ControlledInput
            control={control}
            error={errors.businessName}
            label={t('billings.show.business_name')}
            type="text"
            name="businessName"
            classNameParent="w-full"
            className="w-full"
          />
          <ControlledInput
            control={control}
            error={errors.taxid}
            label={t('billings.show.tax_id')}
            type="text"
            name="taxid"
            classNameParent="w-full"
            className="w-full"
          />
        </div>
      )}
      <div className="split">
        <ControlledInput
          control={control}
          error={errors.invoiceEmails}
          label={t('billings.show.invoice_email')}
          labelButton={t('billings.show.add_another_email')}
          type="multiples-inputs"
          typeMultiples="email"
          name="invoiceEmails"
          classNameParent="w-full"
          className="w-full"
        />
        <ControlledInput
          control={control}
          error={errors.invoiceCcEmails}
          label={t('billings.show.invoice_cc_email')}
          labelButton={t('billings.show.add_another_email')}
          type="multiples-inputs"
          typeMultiples="email"
          name="invoiceCcEmails"
          classNameParent="w-full"
          className="w-full"
        />
      </div>
      {!billingAccountPayload?.currencyConfirmedAt && !isLoading && (
        <ControlledInput
          control={control}
          error={errors.currency}
          label={t('billings.show.currency')}
          type="select"
          options={optionsCurrency}
          defaultOption={defaultOptionCurrency}
          name="currency"
          classNameParent="mb-2"
        />
      )}
      <Button
        label={t('billings.confirm_billing_details')}
        onClick={onSubmit}
        onKeyDown={handlerEnter(onSubmit)}
        size="large"
        className="!mt-6 !flex !items-center"
        variant="contained"
        loading={isLoading}
      />
    </Entering>
  );
};

export default BillingInfoForm;
